/**
 * Utility functions for handling UTM parameters
 */

export interface UTMParameters {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

/**
 * Captures UTM parameters from the current URL
 * @returns Object with UTM parameters or empty object if none found
 */
export function getUTMParameters(): UTMParameters {
  if (typeof window === 'undefined') {
    return {};
  }

  const urlParams = new URLSearchParams(window.location.search);
  const utmParams: UTMParameters = {};

  // Capture all UTM parameters
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  
  utmKeys.forEach((key) => {
    const value = urlParams.get(key);
    if (value) {
      utmParams[key as keyof UTMParameters] = value;
    }
  });

  return utmParams;
}

/**
 * Stores UTM parameters in sessionStorage for persistence across page navigation
 */
export function storeUTMParameters(): void {
  const utmParams = getUTMParameters();
  
  if (Object.keys(utmParams).length > 0) {
    sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
  }
}

/**
 * Retrieves stored UTM parameters from sessionStorage
 * Falls back to current URL parameters if not in storage
 */
export function getStoredUTMParameters(): UTMParameters {
  try {
    const stored = sessionStorage.getItem('utm_params');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error reading UTM parameters from storage:', error);
  }
  
  return getUTMParameters();
}

/**
 * Clears stored UTM parameters from sessionStorage
 */
export function clearUTMParameters(): void {
  sessionStorage.removeItem('utm_params');
}
